// Forecast quantile-band chart: P05–P95 outer band + P10–P90 inner band + P50
// line, optional overlay line (e.g. DAM), dotted actuals, a "now" reference and
// optional spike markers. Click a block to drill in. Built on recharts
// ComposedChart with the stacked-area band trick (transparent base + span).

import { memo, useMemo } from "react";
import {
  Area,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from "recharts";

import { ChartFrame } from "@/components/charts/ChartFrame";
import { usePhone } from "@/app/console/useViewport";
import {
  ANIMATION_ACTIVE,
  AXIS_TICK,
  GRID_PROPS,
  TOOLTIP_CONTENT_STYLE,
  TOOLTIP_LABEL_STYLE,
} from "@/components/charts/theme";

export interface QuantileBlock {
  blockNumber: number;
  deliveryTime?: string;
  p05: number;
  p10: number;
  p50: number;
  p90: number;
  p95: number;
  actual?: number | null;
  spikeProb?: number;
  [key: string]: unknown;
}

export interface QuantileBandChartProps {
  data: ReadonlyArray<QuantileBlock>;
  xKey?: string;
  accentColor?: string;
  overlayKey?: string;
  overlayColor?: string;
  overlayName?: string;
  showActuals?: boolean;
  nowBlock?: number;
  height?: number;
  yTickFormatter?: (v: number) => string;
  tooltipFormatter?: (value: unknown, name: string) => [string, string] | string;
  onBlockClick?: (block: QuantileBlock) => void;
  /** Full-screen heading (defaults to the enclosing Card's title). */
  title?: string;
  expandable?: boolean;
}

interface BandRow extends QuantileBlock {
  _outerBase: number;
  _outerSpan: number;
  _innerBase: number;
  _innerSpan: number;
}

function QuantileBandChartBase({
  data,
  xKey = "blockNumber",
  accentColor = "#E4E4E7",
  overlayKey,
  overlayColor = "#60a5fa",
  overlayName = "DAM",
  showActuals = true,
  nowBlock,
  height = 300,
  yTickFormatter,
  tooltipFormatter,
  onBlockClick,
  title,
  expandable,
}: QuantileBandChartProps) {
  const phone = usePhone();
  const rows = useMemo<BandRow[]>(
    () =>
      data.map((d) => ({
        ...d,
        _outerBase: d.p05,
        _outerSpan: Math.max(0, d.p95 - d.p05),
        _innerBase: d.p10,
        _innerSpan: Math.max(0, d.p90 - d.p10),
      })),
    [data],
  );

  return (
    <ChartFrame height={height} title={title} expandable={expandable}>
      <ComposedChart
        data={rows}
        margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
        // Only wired when the page drills down, so ChartFrame's tap-to-expand
        // (which yields to charts that own a click) stays on for the others.
        onClick={
          onBlockClick
            ? (state) => {
                const i = (state as { activeTooltipIndex?: number | string })?.activeTooltipIndex;
                const p = typeof i === "number" ? rows[i] : undefined;
                if (p) onBlockClick(p);
              }
            : undefined
        }
      >
        <CartesianGrid {...GRID_PROPS} />
        <XAxis dataKey={xKey} tick={AXIS_TICK} tickLine={false} minTickGap={phone ? 56 : 28} interval={phone ? "preserveStartEnd" : undefined} />
        <YAxis tick={AXIS_TICK} tickLine={false} width={56} tickFormatter={yTickFormatter} />
        <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} formatter={tooltipFormatter as never} />

        {/* outer band P05–P95 */}
        <Area dataKey="_outerBase" stackId="outer" stroke="none" fill="transparent" isAnimationActive={false} legendType="none" />
        <Area dataKey="_outerSpan" stackId="outer" stroke="none" fill={accentColor} fillOpacity={0.06} isAnimationActive={false} legendType="none" name="P05–P95" />

        {/* inner band P10–P90 */}
        <Area dataKey="_innerBase" stackId="inner" stroke="none" fill="transparent" isAnimationActive={false} legendType="none" />
        <Area dataKey="_innerSpan" stackId="inner" stroke="none" fill={accentColor} fillOpacity={0.14} isAnimationActive={false} legendType="none" name="P10–P90" />

        {overlayKey && (
          <Line
            dataKey={overlayKey}
            name={overlayName}
            stroke={overlayColor}
            strokeWidth={2}
            strokeDasharray="5 3"
            dot={false}
            isAnimationActive={ANIMATION_ACTIVE}
          />
        )}

        <Line dataKey="p50" name="P50 (median)" stroke={accentColor} strokeWidth={2} dot={false} activeDot={{ r: 4 }} isAnimationActive={ANIMATION_ACTIVE} />

        {showActuals && (
          <Line dataKey="actual" name="Actual" stroke="#e4e4e7" strokeWidth={1.5} strokeDasharray="1 3" dot={false} connectNulls isAnimationActive={ANIMATION_ACTIVE} />
        )}

        {typeof nowBlock === "number" && (
          <ReferenceLine x={nowBlock} stroke="#52525b" strokeDasharray="4 4" label={{ value: "now", fill: "#71717a", fontSize: 10, position: "top" }} />
        )}
      </ComposedChart>
    </ChartFrame>
  );
}

export const QuantileBandChart = memo(QuantileBandChartBase);
